import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { createClient } from '@atlassian/search-client/base-search-client';
import { Scope as JiraScope } from '@atlassian/search-client/jira/types';
import { type User, usersQuery } from '@atlassian/search-client/user/user';

import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type { SelectFilterState } from '../../../../../common/constants/filters/select-filter/types';
import {
	ProductKeys,
	type ProductKeys1P,
	type ProductKeys3P,
} from '../../../../../common/constants/products';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

const searchJiraCloudReporter = async (
	query: string,
	config: CloudConfig,
	options?: { initialOptions: boolean },
) => {
	const headers = buildAtlAttributionHeaders({
		cloudId: config.cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});
	const initialUsersQuery =
		config.userId && options?.initialOptions
			? usersQuery([config.userId], headers)
			: Promise.resolve({ data: { users: [] } });

	const searchClient = createClient({
		cloudId: config.cloudId || '',
	});

	const searchReporterQuery = searchClient.search({
		query,
		resultLimit: 5,
		scopes: [JiraScope.People],
		analytics: {
			identifier: 'search-page',
		},
	});

	const [initialUsersResponse, searchReporterReponse] = await Promise.all([
		initialUsersQuery,
		searchReporterQuery,
	]);
	const searchReporterResults = searchReporterReponse.response.rawData.scopes[0]?.results ?? [];

	const allReporters = [
		// The current user is always first
		...(initialUsersResponse.data?.users.map((user) => ({
			...user,
			id: user.accountId,
		})) ?? []),
		...searchReporterResults.map((user) => ({
			picture: user.avatarUrl,
			name: user.name,
			id: user.id,
			accountId: user.id,
		})),
	] satisfies User[];

	const userIds = new Set();

	return allReporters
		.filter((user) => !userIds.has(user.accountId) && userIds.add(user.accountId)) // Remove duplicates
		.map((user) => ({
			trackingKey: user.accountId,
			value: user.accountId,
			label: user.name,
			avatarUrl: user.picture,
			queryParamValue: user.accountId,
			product: config.product,
		}));
};

const productKeys1P: ProductKeys1P[] = [
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
];

const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('reporter');

const getProducts = () => [...productKeys1P, ...products3PFromConfig];

export const filter: Readonly<{
	id: 'reporter';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'reporter'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'reporter'>;
	getInitialState: (
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'reporter'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'reporter',
	products: getProducts(),
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'jReporter',
		fetcher: async (queryParams, config) => {
			const headers = buildAtlAttributionHeaders({
				cloudId: config.cloudId,
				activationId: config.activationId,
				product: config.primaryProduct,
			});
			const initialUsersResponse = await usersQuery(queryParams, headers);

			const allReporters = initialUsersResponse.data?.users ?? ([] satisfies User[]);

			const userIds = new Set();

			return allReporters
				.filter((user) => !userIds.has(user.accountId) && userIds.add(user.accountId)) // Remove duplicates
				.map((user) => ({
					trackingKey: user.accountId,
					value: user.accountId,
					label: user.name,
					avatarUrl: user.picture,
					queryParamValue: user.accountId,
					product: config.product,
				}));
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return searchJiraCloudReporter('', config, { initialOptions: true });
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			return searchJiraCloudReporter(query, config);
		},
	},
});
